import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { ListBaseModel } from '@/api/models';
import { queryKeyMap as projectQueryKeyMap } from '@/features/project/_utils/query-keys';
import { useInvalidateQuery, useRemoveQueries } from '@/hooks';
import type { z } from 'zod';
import {
  AddGroupTargetFormSchema,
  GroupModel,
  GroupMutationParamsSchema,
  GroupTargetsModel,
  GroupTargetsMutationParamsSchema,
  GroupsModel,
  UpdateGroupMutationParamsSchema,
} from '../_models/group';
import { queryKeyMap } from '../_utils/query-keys';

type Params = Partial<
  Pick<z.infer<typeof GroupModel>, 'targetType' | 'fiscalYear' | 'name'>
>;

const ExtendedGroupsModel = ListBaseModel.extend({
  results: GroupsModel,
});

export const useGroups = <T = z.infer<typeof ExtendedGroupsModel>, U = T>(
  params?: Params,
  select?: (data: z.infer<typeof ExtendedGroupsModel>) => U,
  enabled = true,
) => {
  return useQuery({
    queryKey: queryKeyMap.groups(params),
    queryFn: async () => {
      const groups = await axiosGet('/api/groups/', params);
      return ExtendedGroupsModel.parse(groups);
    },
    select,
    enabled,
  });
};

export const useGroupTargets = <T = z.infer<typeof GroupTargetsModel>>({
  objectId,
  select,
}: {
  objectId: string;
  select?: (data: z.infer<typeof GroupTargetsModel>) => T;
}) => {
  return useQuery({
    queryKey: queryKeyMap.groupTargets(objectId),
    queryFn: async () => {
      const groupTargets = await axiosGet('/api/group-targets/', { objectId });
      return GroupTargetsModel.parse(groupTargets);
    },
    select,
    enabled: !!objectId,
  });
};

export const useGroup = (id: string, params?: object) => {
  return useQuery({
    queryKey: queryKeyMap.group(id),
    queryFn: async () => {
      const group = await axiosGet(`/api/groups/${id}/`, params);
      return GroupModel.parse(group);
    },
    enabled: !!id,
  });
};

export const useCreateGroup = () => {
  const { invalidate } = useInvalidateQuery();

  return useMutation({
    mutationFn: async (values: z.infer<typeof GroupMutationParamsSchema>) => {
      const {
        fiscalYear,
        additionalContext,
        category,
        organizationIds,
        ...payload
      } = GroupMutationParamsSchema.parse(values);
      const res = await axiosPost('/api/groups/', {
        ...payload,
        fiscalYear: fiscalYear.year(),
        additionalContext: {
          ...additionalContext,
          category,
        },
        organizationId: organizationIds.at(-1),
      });
      await invalidate(queryKeyMap.groups());
      return res;
    },
  });
};

export const useUpdateGroup = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof UpdateGroupMutationParamsSchema>,
    ) => {
      const {
        id,
        fiscalYear,
        category,
        additionalContext,
        organizationIds,
        ...payload
      } = UpdateGroupMutationParamsSchema.parse(values);
      const res = await axiosPatch(`/api/groups/${id}/`, {
        ...payload,
        fiscalYear: fiscalYear.year(),
        additionalContext: { ...additionalContext, category },
        organizationId: organizationIds.at(-1),
      });
      await Promise.all([
        invalidate(queryKeyMap.group(id)),
        invalidate(projectQueryKeyMap.projectGroup(id)),
        invalidate(queryKeyMap.groups()),
      ]);
      return res;
    },
  });
};

export const useDeleteGroup = () => {
  const { invalidate } = useInvalidateQuery();
  const { removeQuery } = useRemoveQueries();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axiosDelete(`/api/groups/${id}/`);
      await invalidate(queryKeyMap.groups());
      removeQuery(['group', id]);
      return res;
    },
  });
};

export const useUpdateGroupTargets = (id: string) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof GroupTargetsMutationParamsSchema>,
    ) => {
      const objectIds = GroupTargetsMutationParamsSchema.parse(values);
      const res = await axiosPost(`/api/groups/${id}/group-targets/`, {
        objectIds,
      });
      await Promise.all([
        invalidate(queryKeyMap.groupTargets(id)),
        invalidate(queryKeyMap.group(id)),
        invalidate(projectQueryKeyMap.projectGroupSummary(id)),
        invalidate(projectQueryKeyMap.groupProjects()),
        // for rs
        invalidate(queryKeyMap.groupProjects(id)),
        invalidate(queryKeyMap.groupProjectIds(id)),
      ]);
      return res;
    },
  });
};

export const useAddGroupTarget = (objectId: string) => {
  const { invalidate } = useInvalidateQuery();

  return useMutation({
    mutationFn: async (values: z.infer<typeof AddGroupTargetFormSchema>) => {
      const { groupId } = AddGroupTargetFormSchema.parse(values);
      const res = await axiosGet(`/api/groups/${groupId}/`);
      const group = GroupModel.parse(res);
      const objectIds = [
        ...new Set(group.groupTargets.map((d) => d.objectId).concat(objectId)),
      ];
      return await axiosPost(`/api/groups/${groupId}/group-targets/`, {
        objectIds,
      });
    },
    onSuccess: async () => {
      await invalidate(queryKeyMap.groupTargets(objectId));
    },
  });
};
